/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { ApplicationEditions } from '@jira/platform__application-editions/src/edition.tsx';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { fg } from '@atlassian/jira-feature-gating';
import type { TopNavSettingsMenuEntrypointProps } from './TopNavSettingsMenu.entrypoint.types.tsx';
import TopNavSettingsMenuQuery, {
	type TopNavSettingsMenuQuery as TTopNavSettingsMenuQuery,
} from './__generated__/TopNavSettingsMenuQuery.graphql';

type SettingsMenuEntryPointParams = {
	cloudId: string;
	isAdmin: boolean;
	appEditions?: ApplicationEditions;
};

type MenuQueries = {
	topNavSettingsMenuQuery: TTopNavSettingsMenuQuery;
};

type SettingsMenuEntryPointComponent = EntryPointComponent<
	MenuQueries,
	{},
	TopNavSettingsMenuEntrypointProps,
	{}
>;

export const settingsMenuEntryPoint: EntryPoint<
	SettingsMenuEntryPointComponent,
	SettingsMenuEntryPointParams
> = createEntryPoint({
	root: JSResourceForInteraction<SettingsMenuEntryPointComponent>(
		/* webpackChunkName: "async-top-nav-settings-menu" */ './TopNavSettingsMenu.tsx',
	),
	getPreloadProps: ({ cloudId, isAdmin, appEditions }: SettingsMenuEntryPointParams) => ({
		queries: {
			topNavSettingsMenuQuery: {
				options: { fetchPolicy: 'store-and-network' as const },
				parameters: TopNavSettingsMenuQuery,
				variables: {
					cloudId,
					// We can skip the permission check when:
					// 1. The user is an admin (because admins can automatically manage custom onboarding configs), or
					// 2. Upsell is enabled (because upsell is only available to admins), or
					// 3. The main custom onboarding hub gate is disabled (because the custom onboarding feature won't be visible to anyone)
					skipDelegatedAdminCheck:
						isAdmin ||
						appEditions?.software !== 'PREMIUM_EDITION' ||
						!fg('jira-onboarding-hub-gate'),
					// The JSM app-admin scoped settings group is only shown to non-admins, so full admins
					// never need the lookup. It is also gated behind `enable_jsm_admin`.
					skipJsmAdminCheck: isAdmin || !fg('enable_jsm_admin'),
				},
			},
		},
	}),
});
